import HTTP from './mav';

/**
 * Record an edit (user action) for a batch item
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index within the batch (0-based)
 * @param {object} editData - Edit details
 * @returns {Promise}
 */
export function recordBatchItemEdit(batchId, itemIndex, editData) {
  return HTTP.post(`/predict/domain/batch/async/${batchId}/items/${itemIndex}/edits`, editData, {
    niSkipFlash: true,
  });
}

/**
 * Record a parent creation edit
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} parentData - Parent creation details
 * @returns {Promise}
 */
export function recordParentCreation(batchId, itemIndex, parentData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'parent_created',
    parent_id: parentData.parent_id,
    parent_name: parentData.parent_name,
    proposal_id: parentData.proposal_id || null,
    status: parentData.status || 'pending',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a brand creation edit
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} brandData - Brand creation details
 * @returns {Promise}
 */
export function recordBrandCreation(batchId, itemIndex, brandData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'brand_created',
    parent_id: brandData.parent_id,
    brand_id: brandData.brand_id,
    brand_name: brandData.brand_name,
    proposal_id: brandData.proposal_id || null,
    status: brandData.status || 'pending',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a channel creation edit
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} channelData - Channel creation details
 * @returns {Promise}
 */
export function recordChannelCreation(batchId, itemIndex, channelData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'channel_created',
    parent_id: channelData.parent_id || null,
    brand_id: channelData.brand_id,
    channel_id: channelData.channel_id,
    channel_name: channelData.channel_name,
    proposal_id: channelData.proposal_id || null,
    status: channelData.status || 'pending',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a tree (parent + brand + channel) creation edit
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} treeData - Tree creation details
 * @returns {Promise}
 */
export function recordTreeCreation(batchId, itemIndex, treeData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'hierarchy_created',
    parent_id: treeData.parent_id,
    parent_name: treeData.parent_name,
    brand_id: treeData.brand_id,
    brand_name: treeData.brand_name,
    channel_id: treeData.channel_id,
    channel_name: treeData.channel_name,
    tree_proposal_id: treeData.tree_proposal_id || null,
    status: treeData.status || 'pending',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a proposal submission edit
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} proposalData - Proposal details
 * @returns {Promise}
 */
export function recordProposalSubmission(batchId, itemIndex, proposalData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'prediction_accepted',
    tree_id: proposalData.tree_id,
    discovery_id: proposalData.discovery_id || null,
    proposal_id: proposalData.proposal_id || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a prediction override edit (operator picks correct existing P→B→C)
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} overrideData - Override details with parent/brand/channel IDs and names
 * @returns {Promise}
 */
export function recordPredictionOverride(batchId, itemIndex, overrideData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'prediction_override',
    parent_id: overrideData.parent_id,
    parent_name: overrideData.parent_name || '',
    brand_id: overrideData.brand_id,
    brand_name: overrideData.brand_name || '',
    channel_id: overrideData.channel_id || 0,
    channel_name: overrideData.channel_name || '',
    rationale: overrideData.rationale || '',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Record a rule creation edit (ANT rule was created for this domain)
 * @param {string} batchId - Batch UUID
 * @param {number} itemIndex - Item index
 * @param {object} ruleData - Rule details
 * @returns {Promise}
 */
export function recordRuleCreation(batchId, itemIndex, ruleData) {
  return recordBatchItemEdit(batchId, itemIndex, {
    action_type: 'rule_created',
    rule_id: ruleData.rule_id,
    proposal_id: ruleData.proposal_id || null,
    rule_type: ruleData.rule_type || null,
    timestamp: new Date().toISOString(),
  });
}
